import React from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import { formatIqd } from '../../utils/money';
import { formatDateShort } from '../../utils/date';
import { cityLabel } from '../../utils/cities';
import { formatIraqPhone } from '../../utils/phone';
import { CUSTOMER_PALETTES, WHATSAPP_GREEN } from '../../theme/brandColors';
import { ColumnDef } from '@tanstack/react-table';
import { generatePath } from 'react-router-dom';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import format from 'date-fns/format';
import { ar as dateFnsAr } from 'date-fns/locale';
import isValid from 'date-fns/isValid';
import { GetCustomersResponseSingleItem } from '../../setup/apiTypes/customers';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import ButtonLink from '../../components/atoms/Button/buttonLink';
import routes from '../../setup/consts/routes';
import { IconWhatsApp } from '../../assets/svg/icons';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import {
  StyledActionCell,
  StyledCustomerCell,
  StyledCustomerInfo,
  StyledCustomerName,
  StyledCustomerSubtitle,
  StyledOrderCount,
  StyledPhoneCell,
  StyledValueCell,
} from './styles';

const _hashHue = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return Math.abs(h);
};

const _getInitials = (name?: string): string => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const CustomerAvatar = ({ name, size = 36 }: { name: string; size?: number }) => {
  const hue = _hashHue(name);
  return (
    <span
      style={{
        width: size, height: size, borderRadius: '50%',
        background: `oklch(0.85 0.04 ${hue})`,
        color: `oklch(0.32 0.06 ${hue})`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.floor(size * 0.33), fontWeight: 700, flexShrink: 0, lineHeight: 1,
      }}
    >
      {_getInitials(name) || '·'}
    </span>
  );
};

export const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const nameHash = (name: string): number => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return h;
};

export const avatarPalette = (name: string) =>
  CUSTOMER_PALETTES[nameHash(name) % CUSTOMER_PALETTES.length];


export const customersTableFakeData: GetCustomersResponseSingleItem[] = [
  {
    id: Math.random().toString(),
    name: 'Abas Hanoon',
    phone: '96403458981',
    lastOrderDate: new Date('01/02/2020'),
    addresses: [{ state: 'Baghdad' }],
    totalOrders: 320,
    totalPrice: 260000,
  },
];

export const customersTableColumns = (
  intl: IntlShape
): ColumnDef<GetCustomersResponseSingleItem>[] => [
  {
    header: intl.formatMessage({
      id: 'customers.customerName',
      defaultMessage: 'Customer',
    }),
    accessorKey: 'name',
    size: 190,
    cell: ({ row: { original } }) => {
      const date = new Date(original.lastOrderDate);
      const dateValid = isValid(date);
      const locale = intl.locale === 'ar' ? dateFnsAr : undefined;
      const relative = dateValid ? formatDistanceToNow(date, { addSuffix: true, locale }) : null;
      const full = dateValid ? formatDateShort(date, intl.locale) : null;
      return (
        <StyledCustomerCell>
          <CustomerAvatar name={original.name || ''} size={36} />
          <StyledCustomerInfo>
            <StyledCustomerName>
              {original.name || <em style={{ opacity: 0.4 }}>{intl.formatMessage({ id: 'customers.unnamed', defaultMessage: 'Unnamed' })}</em>}
            </StyledCustomerName>
            {relative && (
              <StyledCustomerSubtitle title={full ?? undefined}>
                {intl.formatMessage({ id: 'customers.lastOrder', defaultMessage: 'Last order' })}&nbsp;·&nbsp;{relative}
              </StyledCustomerSubtitle>
            )}
          </StyledCustomerInfo>
        </StyledCustomerCell>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: 'customers.customerPhone',
      defaultMessage: 'Phone',
    }),
    accessorKey: 'phone',
    size: 110,
    cell: ({ cell: { getValue } }) => {
      const phone = getValue() as string;
      if (!phone) return <span style={{ opacity: 0.4 }}>—</span>;
      const formatted = formatIraqPhone(phone);
      return (
        <StyledPhoneCell
          href={`tel:${phone}`}
          title={phone}
          onClick={(e) => e.stopPropagation()}
        >
          {formatted}
        </StyledPhoneCell>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: 'customers.city',
      defaultMessage: 'City',
    }),
    id: 'city',
    accessorFn: (row) => row.city || row.addresses?.[0]?.state || '',
    size: 110,
    cell: ({ cell: { getValue } }) => {
      const city = getValue() as string;
      if (!city) return <span style={{ opacity: 0.4 }}>—</span>;
      return <Badge tone={BadgeTone.Neutral} dot>{cityLabel(city, intl.locale as 'en' | 'ar')}</Badge>;
    },
  },
  {
    header: intl.formatMessage({
      id: 'customers.totalOrders',
      defaultMessage: 'Orders',
    }),
    accessorKey: 'totalOrders',
    size: 80,
    cell: ({ cell: { getValue } }) => {
      const v = (getValue() as number) ?? 0;
      return (
        <StyledOrderCount>
          <strong style={{ color: v <= 1 ? 'var(--muted)' : 'var(--ink)' }}>
            {v.toLocaleString()}
          </strong>
          <span>{v === 1 ? intl.formatMessage({ id: 'customers.orderSingular', defaultMessage: 'order' }) : intl.formatMessage({ id: 'customers.orderPlural', defaultMessage: 'orders' })}</span>
        </StyledOrderCount>
      );
    },
  },
  {
    header: () => (
      <div style={{ textAlign: 'right' }}>
        {intl.formatMessage({ id: 'customers.valueIQD', defaultMessage: 'Lifetime value' })}
      </div>
    ),
    accessorKey: 'totalPrice',
    size: 130,
    cell: ({ cell: { getValue } }) => {
      const v = (getValue() as number) ?? 0;
      return (
        <StyledValueCell style={{ justifyContent: 'flex-end' }}>
          <strong style={{ fontWeight: 700 }}>{formatIqd(v)}</strong>
          <span>IQD</span>
        </StyledValueCell>
      );
    },
  },
  {
    header: '',
    id: 'action',
    size: 160,
    cell: ({ row: { original } }) => (
      <StyledActionCell>
        <ButtonLink
          kind={ButtonKind.Ghost}
          size={ButtonSize.Small}
          onClick={(e) => {
            e.stopPropagation();
          }}
          to={generatePath(routes.PLACE_CUSTOMER_ORDER, {
            customerId: original.id,
          })}
        >
          <FormattedMessage
            id='customers.createOrder'
            defaultMessage='Create order'
          />
        </ButtonLink>
        {original.phone && (
          <a
            href={`https://wa.me/${original.phone.replace(/\D/g, '')}`}
            target='_blank'
            rel='noopener noreferrer'
            aria-label='WhatsApp'
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 'var(--r-sm)',
              color: WHATSAPP_GREEN,
              flexShrink: 0,
              transition: 'background var(--dur-fast) var(--ease-out)',
            }}
          >
            <IconWhatsApp style={{ width: 18, height: 18 }} />
          </a>
        )}
      </StyledActionCell>
    ),
  },
];
